import { Controller } from "@hotwired/stimulus"

// Change Orders: list page (new-CO modal bundling approved PCOs) and the
// detail page (add/remove PCOs, title autosave, lifecycle actions).
export default class extends Controller {
  static targets = ["newModal", "title", "pcoCheck", "createBtn",
                    "rejectModal", "rejectReason", "deleteModal", "reopenModal", "reopenReason", "total", "days",
                    "attachmentList"]
  static values = {
    createUrl: String, updateUrl: String, submitUrl: String,
    approveUrl: String, rejectUrl: String, reviseUrl: String, reopenUrl: String, deleteUrl: String,
    presignUrl: String, registerUrl: String, attachmentsUrl: String
  }

  connect() {
    const msg = sessionStorage.getItem("builder-finance.flash")
    if (msg) {
      sessionStorage.removeItem("builder-finance.flash")
      this._showToast(msg)
    }
    if (this.hasAttachmentListTarget) this._loadAttachments()
  }

  openCo(e) {
    if (e.target.closest("button, a, input")) return
    window.location.href = e.currentTarget.dataset.url
  }

  openNewModal() { this.newModalTarget.classList.remove("hidden") }
  closeNewModal() { this.newModalTarget.classList.add("hidden") }
  openRejectModal() { this.rejectModalTarget.classList.remove("hidden") }
  openDeleteModal() { this.deleteModalTarget.classList.remove("hidden") }
  openReopenModal() { this.reopenModalTarget.classList.remove("hidden") }
  closeModals() {
    ;["newModal", "rejectModal", "deleteModal", "reopenModal"].forEach((m) => {
      if (this[`has${m[0].toUpperCase()}${m.slice(1)}Target`]) this[`${m}Target`].classList.add("hidden")
    })
  }
  stopPropagation(e) { e.stopPropagation() }

  async create() {
    if (!this.titleTarget.value.trim()) return this._flagEmptyTitle(this.titleTarget.dataset.message)

    this.createBtnTarget.disabled = true
    try {
      const pcoIds = this.pcoCheckTargets.filter((c) => c.checked).map((c) => c.value)
      const resp = await fetch(this.createUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ title: this.titleTarget.value, pco_ids: pcoIds })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not create the change order")
      window.location.href = data.url
    } catch (err) {
      this._showToast(err.message, "alert")
      this.createBtnTarget.disabled = false
    }
  }

  async saveTitle() {
    const input = this.titleTarget
    const resp = await fetch(this.updateUrlValue, {
      method: "PATCH", headers: this._headers(),
      body: JSON.stringify({ title: input.value })
    })
    if (resp.ok) {
      input.defaultValue = input.value
      input.classList.remove("border-red-400", "ring-1", "ring-red-400")
    } else {
      const data = await resp.json().catch(() => ({}))
      input.value = input.defaultValue
      this._showToast(data.error || "Could not save the title", "alert")
    }
  }

  // Runs before chain-builder#open on Approve & Send: an untitled CO shows
  // as "Untitled" everywhere once it's out for signature (KC-107).
  requireTitle(e) {
    if (!this.hasTitleTarget || this.titleTarget.value.trim()) return

    e.stopImmediatePropagation()
    this._flagEmptyTitle(e.currentTarget.dataset.message)
  }

  _flagEmptyTitle(message) {
    const input = this.titleTarget
    input.classList.add("border-red-400", "ring-1", "ring-red-400")
    input.focus()
    this._showToast(message, "alert")
  }

  async addPco(e) { await this._bundle(e.currentTarget.dataset.url, "POST") }
  async removePco(e) { await this._bundle(e.currentTarget.dataset.url, "DELETE") }

  async _bundle(url, method) {
    const resp = await fetch(url, { method, headers: this._headers() })
    const data = await resp.json()
    if (!resp.ok) return this._showToast(data.error || "Could not update the bundle", "alert")
    window.location.reload()
  }

  async submit() { await this._post(this.submitUrlValue, {}, "Submitted — the owner has it now") }
  async approve() { await this._post(this.approveUrlValue, {}, "Approved — contract sum updated, flows into the next pay app") }
  async reject() { await this._post(this.rejectUrlValue, { reason: this.rejectReasonTarget.value }, "Rejected") }
  async revise() { await this._post(this.reviseUrlValue, {}, "Back to draft — make your changes and resend for signature") }
  async reopenCo() { await this._post(this.reopenUrlValue, { reason: this.reopenReasonTarget.value }, "Signatures withdrawn — back to draft, contract sum rolled back") }

  // Trash on a CO that can't be deleted right now (not the latest, or fully
  // signed) — clicking explains why instead of no-opping, like on PRs.
  deleteBlocked(e) {
    this._showToast(e.currentTarget.dataset.message || "This change order can't be deleted right now.", "alert")
  }

  // Hard delete — the CO page is gone afterwards, so follow the server's
  // redirect to the CO list instead of reloading in place.
  async deleteCo() {
    try {
      const resp = await fetch(this.deleteUrlValue, { method: "DELETE", headers: this._headers() })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Could not delete the change order")
      sessionStorage.setItem("builder-finance.flash", "Change order deleted — its proposals are released")
      window.location.href = data.redirect
    } catch (err) {
      this.closeModals()
      this._showToast(err.message, "alert")
    }
  }

  // ── Supporting documents (same flow as the PCO page) ──────────────────────
  async filesChosen(e) {
    const files = Array.from(e.target.files || [])
    e.target.value = ""
    await this._uploadFileList(files)
  }

  dragOver(e) {
    e.preventDefault()
    e.currentTarget.classList.add("ring-2", "ring-brand", "bg-brand/5")
  }

  dragLeave(e) {
    e.currentTarget.classList.remove("ring-2", "ring-brand", "bg-brand/5")
  }

  async dropFiles(e) {
    e.preventDefault()
    e.currentTarget.classList.remove("ring-2", "ring-brand", "bg-brand/5")
    await this._uploadFileList(Array.from(e.dataTransfer?.files || []))
  }

  async _uploadFileList(files) {
    for (const file of files) {
      try {
        const presign = await fetch(this.presignUrlValue, {
          method: "POST", headers: this._headers(),
          body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
        })
        const pd = await presign.json()
        if (!presign.ok) throw new Error(pd.error || "Upload refused")
        const u = pd.uploads[0]
        await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
        await fetch(this.registerUrlValue, {
          method: "POST", headers: this._headers(),
          body: JSON.stringify({ files: [{ cache_name: u.cache_name, filename: u.filename }] })
        })
      } catch (err) {
        this._showToast(`${file.name}: ${err.message}`, "alert")
      }
    }
    this._loadAttachments()
  }

  async _loadAttachments() {
    if (!this.hasAttachmentListTarget) return
    try {
      const resp = await fetch(this.attachmentsUrlValue, { headers: { "Accept": "application/json" } })
      const files = await resp.json()
      this.attachmentListTarget.innerHTML = files.length ? "" : '<span class="text-[13px] text-gray-400">No files attached.</span>'
      files.forEach((f) => this.attachmentListTarget.insertAdjacentHTML("beforeend", this._chipHtml(f)))
    } catch {
      this.attachmentListTarget.innerHTML = '<span class="text-[13px] text-gray-400">Could not load files.</span>'
    }
  }

  _chipHtml(f) {
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    return `<span class="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-lg px-2.5 py-1 text-[13px]" data-attachment-id="${f.id}">
      <a href="${f.url}" target="_blank" class="hover:text-brand max-w-[180px] truncate">${esc(f.filename)}</a>
      <button data-action="click->gc-co#removeAttachment" data-id="${f.id}" class="text-gray-400 hover:text-red-500 cursor-pointer">✕</button>
    </span>`
  }

  async removeAttachment(e) {
    // Grab the chip BEFORE awaiting — e.currentTarget is null once the event
    // finishes dispatching, so reading it after the fetch silently fails.
    const chip = e.currentTarget.closest("[data-attachment-id]")
    const id = e.currentTarget.dataset.id
    const resp = await fetch(`${this.attachmentsUrlValue}/${id}`, { method: "DELETE", headers: this._headers() })
    if (resp.ok) {
      chip?.remove()
      if (this.hasAttachmentListTarget && !this.attachmentListTarget.querySelector("[data-attachment-id]")) {
        this.attachmentListTarget.innerHTML = '<span class="text-[13px] text-gray-400">No files attached.</span>'
      }
    } else {
      this._showToast("Could not remove the file — try again.", "alert")
    }
  }

  async _post(url, body, message) {
    try {
      const resp = await fetch(url, { method: "POST", headers: this._headers(), body: JSON.stringify(body) })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Action failed")
      sessionStorage.setItem("builder-finance.flash", message)
      window.location.reload()
    } catch (err) {
      this.closeModals()
      this._showToast(err.message, "alert")
    }
  }

  _headers() {
    return {
      "Content-Type": "application/json", "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _showToast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    div.textContent = message
    document.body.prepend(div)
  }
}
